import { Extension } from '@tiptap/react';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey, type EditorState } from '@tiptap/pm/state';
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view';
import { DOCUMENT_AGENT_FLASH_MS } from '@cdf/file-core/browser';

/**
 * Briefly highlights content the document agent just changed, as in Solution Studio.
 * People who prefer reduced motion get a static highlight. Decorations are view-only.
 */
export const AGENT_CHANGE_FLASH_MS = DOCUMENT_AGENT_FLASH_MS;
export interface AgentChangeRange { from: number; to: number }
export type AgentChangeFlashMeta = { type: 'show'; id: string; ranges: readonly AgentChangeRange[] } | { type: 'clear'; id: string };
interface AgentChangeFlashState { decorations: DecorationSet; ids: readonly string[] }

export const agentChangeFlashKey = new PluginKey<AgentChangeFlashState>('agentChangeFlash');

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function flashDecorations(doc: ProseMirrorNode, id: string, ranges: readonly AgentChangeRange[], reduced: boolean): Decoration[] {
  const size = doc.content.size, mode = reduced ? 'static' : 'flash';
  const attrs = { class: reduced ? 'agent-change-flash rounded-sm bg-foreground/15' : 'agent-change-flash rounded-sm bg-foreground/15 animate-pulse', 'data-agent-change': mode };
  const spec = { agentChange: id };
  const decorations: Decoration[] = [];
  for (const range of ranges) {
    const from = Math.max(0, Math.min(range.from, size)), to = Math.max(from, Math.min(range.to, size));
    if (to === from) {
      decorations.push(Decoration.widget(from, () => {
        const marker = document.createElement('span');
        marker.className = attrs.class + ' inline-block h-[1em] w-0.5 align-middle';
        marker.setAttribute('data-agent-change', mode);
        marker.setAttribute('aria-hidden', 'true');
        return marker;
      }, { ...spec, side: -1 }));
      continue;
    }
    decorations.push(Decoration.inline(from, to, attrs, spec));
    doc.nodesBetween(from, to, (node, pos) => {
      if (node.isBlock && node.isAtom && pos >= from && pos + node.nodeSize <= to) decorations.push(Decoration.node(pos, pos + node.nodeSize, attrs, spec));
      return !node.isAtom;
    });
  }
  return decorations;
}

export function createAgentChangeFlashPlugin(options: { durationMs?: number; reducedMotion?: () => boolean } = {}) {
  const duration = options.durationMs ?? AGENT_CHANGE_FLASH_MS;
  const reducedMotion = options.reducedMotion ?? prefersReducedMotion;
  return new Plugin<AgentChangeFlashState>({
    key: agentChangeFlashKey,
    state: {
      init: () => ({ decorations: DecorationSet.empty, ids: [] }),
      apply(tr, value) {
        let decorations = value.decorations.map(tr.mapping, tr.doc), ids = value.ids;
        const meta = tr.getMeta(agentChangeFlashKey) as AgentChangeFlashMeta | undefined;
        if (meta) {
          decorations = decorations.remove(decorations.find(undefined, undefined, spec => spec.agentChange === meta.id));
          if (meta.type === 'show') {
            decorations = decorations.add(tr.doc, flashDecorations(tr.doc, meta.id, meta.ranges, reducedMotion()));
            if (!ids.includes(meta.id)) ids = [...ids, meta.id];
          } else ids = ids.filter(id => id !== meta.id);
        }
        return { decorations, ids };
      },
    },
    props: { decorations: state => agentChangeFlashKey.getState(state)?.decorations },
    view(view) {
      const timers = new Map<string, ReturnType<typeof setTimeout>>();
      const schedule = (current: EditorView) => {
        const ids = agentChangeFlashKey.getState(current.state)?.ids ?? [];
        for (const [id, timer] of timers) if (!ids.includes(id)) { clearTimeout(timer); timers.delete(id); }
        for (const id of ids) {
          if (timers.has(id)) continue;
          timers.set(id, setTimeout(() => { timers.delete(id); if (!current.isDestroyed) clearAgentChangeFlash(current, id); }, duration));
        }
      };
      schedule(view);
      return { update: schedule, destroy() { timers.forEach(clearTimeout); timers.clear(); } };
    },
  });
}

export function isAgentChangeFlashing(state: EditorState, id: string): boolean {
  return agentChangeFlashKey.getState(state)?.ids.includes(id) ?? false;
}

export function showAgentChangeFlash(view: EditorView, id: string, ranges: readonly AgentChangeRange[]): boolean {
  if (!agentChangeFlashKey.getState(view.state)) return false;
  view.dispatch(view.state.tr.setMeta(agentChangeFlashKey, { type: 'show', id, ranges } satisfies AgentChangeFlashMeta).setMeta('addToHistory', false));
  return true;
}

export function clearAgentChangeFlash(view: EditorView, id: string): void {
  if (!isAgentChangeFlashing(view.state, id)) return;
  view.dispatch(view.state.tr.setMeta(agentChangeFlashKey, { type: 'clear', id } satisfies AgentChangeFlashMeta).setMeta('addToHistory', false));
}

export const AgentChangeFlash = Extension.create<{ durationMs: number }>({
  name: 'agentChangeFlash',
  addOptions: () => ({ durationMs: AGENT_CHANGE_FLASH_MS }),
  addProseMirrorPlugins() { return [createAgentChangeFlashPlugin({ durationMs: this.options.durationMs })]; },
});
